import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DeviceEventEmitter, ScrollView } from 'react-native';

import { eventItemMatchesDirections } from '../constants/directions';
import { api } from '../services/api';
import { getCachedTimezone, getUserTimezone } from '../services/timezone';
import { CalendarItem, CurrentLiveResponse, DailyRhythmItem, RoomResponse, UnifiedEvent, User } from '../types/auth';
import { resolveName } from '../utils/displayName';
import type { ShareEntity } from '../utils/roomShare';
import { useCalendar } from '../context/CalendarContext';
import { getItemSpanDaysISO } from '../utils/calendarSpan';
import { isRoomInWindow, mergeCalendarRooms } from '../utils/calendarRooms';
import { isServerConfirmedEnded } from '../utils/effectiveRoomStatus';
import { isPublicDiscoverableEvent } from '../utils/ownPublications';
import { LIVE_SESSION_TERM } from '../utils/liveSessionDisplay';
import { useDirections } from './useDirections';
import { useLivePracticesEnabled } from './useFeatureFlags';

export type FormatKey = 'offline' | 'online' | 'livestream' | 'liveroom';

export type LivesCalendarItem = DailyRhythmItem & {
  _roomId?: number;
  /** CORE-1865 — анонс сессии в ленте (CORE-1844); null — репостится сама сессия. */
  _announcementPostId?: number | null;
  /** CORE-1865 — приватную сессию наружу не репостят (сервер ответил бы 403). */
  _roomPrivate?: boolean;
  _isMine?: boolean;
  _isMyRoom?: boolean;
  /** CORE-1134 — текущий пользователь ведёт эту Live-практику. */
  _isHost?: boolean;
  _offlineEvent?: UnifiedEvent;
  /**
   * CORE-1264 — auth user id of the event's master. Watch-party payloads on
   * prod carry only `hostUserId` (no `hostMasterProfileId`), so this is the
   * only reliable way to reach the master's discussions from a room card.
   */
  _masterUserId?: string | null;
  /** CORE-1737 — обсуждение комнаты (`RoomResponse.discussionId`); у офлайн-события
   *  оно лежит в `_offlineEvent.discussionId`. */
  _discussionId?: string | null;
  /**
   * CORE-879 — Live-сессия попала в календарь вместе с купленной программой
   * (`byProgram` из `/api/user/my-calendar`), а не по нажатой кнопке. Строки
   * подписки под ней нет: убрать её из календаря нельзя, она уходит вместе с
   * доступом к программе.
   */
  _byProgram?: boolean;
};

export type CalendarDateInfo = {
  year: number;
  month: number;
  day: number;
  dateISO: string;
};

export type MonthRef = { year: number; month: number };

/**
 * Header label for the day strip. Reflects the month(s) whose days are visible:
 * one month → «Июнь 2026», two in the same year → «Июнь – Июль 2026», two across
 * a year boundary → «Декабрь 2026 – Январь 2027». `monthNames` is the localised
 * full-name array (MONTH_NAMES / MONTH_NAMES_RU / RU_MONTHS_FULL).
 */
export const buildVisibleMonthsTitle = (months: MonthRef[], monthNames: string[]): string => {
  if (months.length === 0) return '';
  const first = months[0];
  const last = months[months.length - 1];
  if (first.year === last.year && first.month === last.month) {
    return `${monthNames[first.month]} ${first.year}`;
  }
  if (first.year === last.year) {
    return `${monthNames[first.month]} – ${monthNames[last.month]} ${first.year}`;
  }
  return `${monthNames[first.month]} ${first.year} – ${monthNames[last.month]} ${last.year}`;
};

export const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export const MONTH_NAMES_RU = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
export const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const DAY_NAMES_RU = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];

export const dateToISO = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

export const toCalendarISO = (y: number, m: number, d: number) =>
  dateToISO(new Date(y, m, d));

export const getItemDateISO = (item: Pick<DailyRhythmItem, 'startDateTimeUtc'>): string | null => {
  const time = new Date(item.startDateTimeUtc).getTime();
  if (!Number.isFinite(time)) return null;
  return dateToISO(new Date(time));
};

export const normalizeFormat = (raw: string | null | undefined): FormatKey | null => {
  if (!raw) return null;
  const v = raw.toLowerCase().trim();
  if (v === 'offline' || v === 'офлайн') return 'offline';
  if (v === 'online' || v === 'онлайн') return 'online';
  if (v === 'livestream' || v === 'лайв-стрим' || v === 'лайв стрим' || v === 'live stream') return 'livestream';
  if (v === 'liveroom' || v === 'лайв-практика' || v === 'лайв практика' || v === 'live practice') return 'liveroom';
  return null;
};

export const itemFormatKey = (item: { kind?: { code?: string } | null; format?: string | null } | null | undefined): FormatKey | null => {
  if (!item) return null;
  const code = item.kind?.code;
  if (code === 'Offline') return 'offline';
  if (code === 'LiveStream') return 'livestream';
  if (code === 'LivePractice') return 'liveroom';
  return normalizeFormat(item.format);
};

export const getRoomItemId = (item: LivesCalendarItem): number | null => {
  if (item._roomId != null) return item._roomId;
  return item.kind?.code === 'LivePractice' ? item.id : null;
};

/**
 * CORE-743 — what a calendar card points at when it is shared: the same
 * routing the card's own tap uses (room announcement / live detail / offline
 * event modal), so the link always lands on the card the user was looking at.
 * `shareEntity` turns this into the localized text + URL.
 */
export const getCalendarShareEntity = (item: LivesCalendarItem): ShareEntity => {
  if (item._offlineEvent) {
    return { type: 'event', id: item._offlineEvent.id, title: item.title };
  }
  const roomId = getRoomItemId(item);
  if (roomId != null) return { type: 'room', id: roomId, title: item.title };
  return { type: 'live', id: item.id, title: item.title };
};

/**
 * Discussion-thread coordinates for a calendar item: the entity id + type used
 * to find its chat discussion (`linkedEntityId` + `linkedEntityType`) and the
 * master profile id. Feeds the EventChatIcon / openEventDiscussion deep-link.
 */
export const getCalendarItemDiscussionRef = (
  item: LivesCalendarItem,
): {
  refId: number;
  linkedEntityType: string;
  masterProfileId: number | null;
  masterUserId: string | null;
  /** CORE-1654 — the offline event carries its discussion id itself;
   *  CORE-1737 — so does a room (`RoomResponse.discussionId`). */
  discussionId?: string | null;
} => {
  // Mappers above fill `master.id` with 0 when the payload has no profile id
  // (CORE-1264: prod watch-parties). 0 is not a profile — never send it on.
  const profileId = (id: number | null | undefined) => (id != null && id > 0 ? id : null);
  const masterUserId = item._masterUserId ?? null;
  if (item._offlineEvent) {
    return {
      refId: item._offlineEvent.id,
      linkedEntityType: 'master_event',
      masterProfileId: profileId(item._offlineEvent.masterProfileId) ?? profileId(item.master?.id),
      masterUserId,
      discussionId: item._offlineEvent.discussionId ?? null,
    };
  }
  const roomId = getRoomItemId(item);
  if (roomId != null) {
    return {
      refId: roomId,
      linkedEntityType: 'watch_party',
      masterProfileId: profileId(item.master?.id),
      masterUserId,
      discussionId: item._discussionId ?? null,
    };
  }
  return { refId: item.id, linkedEntityType: 'live', masterProfileId: profileId(item.master?.id), masterUserId };
};

export const getEventItemKey = (item: LivesCalendarItem): string => {
  if (item._offlineEvent) return `offline-${item._offlineEvent.source}-${item._offlineEvent.id}`;
  const roomId = getRoomItemId(item);
  if (roomId != null) return `room-${roomId}`;
  return `live-${item.id}`;
};

export const mergeUniqueEvents = (
  existing: LivesCalendarItem[],
  incoming: LivesCalendarItem[],
): LivesCalendarItem[] => {
  const seen = new Set(existing.map(getEventItemKey));
  const next = [...existing];
  incoming.forEach(item => {
    const key = getEventItemKey(item);
    if (seen.has(key)) return;
    seen.add(key);
    next.push(item);
  });
  return next;
};

const getDaysInMonth = (y: number, m: number) => new Date(y, m + 1, 0).getDate();

const getOwnMasterDisplayName = (user: User) =>
  user.resolvedDisplayName || user.displayName || 'Master';

// Метка комнаты в календаре — одна на все источники (CORE-1712: у комнат из
// daily-rhythm сервер присылает свою, «Лайв-практика»). CORE-1865 — код
// технический и остаётся, подпись — единый термин: комната и эфир для человека
// одна сущность «Live-сессия», отдельного продукта «Live-практика» нет.
const ROOM_KIND = { code: 'LivePractice', labelEn: 'Live session', labelRu: LIVE_SESSION_TERM, label: LIVE_SESSION_TERM };

export const roomToDailyRhythmItem = (r: RoomResponse, user: User | null): LivesCalendarItem | null => {
  if (!r.scheduledAtUtc) return null;
  const startMs = new Date(r.scheduledAtUtc).getTime();
  const durMs = (r.durationMinutes || 60) * 60_000;
  const now = Date.now();
  // CORE-1290 — тот же предикат, что у R0 в `computeEffectiveStatus`: он ловит
  // и `Cancelled` (мягкая отмена штампует `endedAtUtc`, CORE-833/838), которую
  // сравнение с одним 'Ended' пропускало.
  const isEnded = isServerConfirmedEnded(r) || (startMs + durMs) < now;
  const isLive = !isEnded && startMs - 60_000 <= now && now < startMs + durMs;
  const status: DailyRhythmItem['status'] = isEnded ? 'ended' : isLive ? 'live' : 'upcoming';
  const isHost = !!(user?.id && r.hostUserId === user.id);
  const isMine = isHost || r.isSubscribed === true;

  return {
    id: r.id,
    title: r.title || (r as any).videoPracticeTitle || LIVE_SESSION_TERM,
    startDateTimeUtc: r.scheduledAtUtc,
    localStartTime: new Date(r.scheduledAtUtc).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', hour12: false }),
    durationMinutes: r.durationMinutes ?? null,
    coverImageUrl: r.coverImageUrl || (r as any).videoPracticeCoverImageUrl || null,
    previewUrl: null,
    status,
    master: {
      id: r.hostMasterProfileId ?? (isHost ? user?.masterProfileId ?? 0 : 0),
      name: isHost && user ? getOwnMasterDisplayName(user) : resolveName(r.hostName),
      photoUrl: (isHost ? user?.avatarUrl : null) || r.hostAvatarUrl || null,
      description: '',
      shortBio: '',
    } as any,
    category: (r as any).category ?? null,
    categories: Array.isArray((r as any).categories) ? (r as any).categories : null,
    format: 'liveroom',
    kind: { ...ROOM_KIND } as any,
    _roomId: r.id,
    _announcementPostId: r.announcementPostId ?? null,
    _roomPrivate: r.visibility === 'Private' || (r.visibility == null && r.isPublic === false),
    _isMine: isMine,
    _isMyRoom: isMine,
    _isHost: isHost,
    _masterUserId: r.hostUserId ?? null,
    _discussionId: r.discussionId ?? null,
  };
};

const currentLiveToDailyRhythmItem = (live: CurrentLiveResponse): LivesCalendarItem => ({
  id: live.id,
  title: live.title,
  startDateTimeUtc: live.startDateTimeUtc,
  localStartTime: new Date(live.startDateTimeUtc).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', hour12: false }),
  durationMinutes: live.durationMinutes ?? null,
  coverImageUrl: live.coverImageUrl,
  previewUrl: null,
  status: 'live',
  master: live.master,
  category: null,
  categories: null,
  format: 'livestream',
  kind: { code: 'LiveStream', labelEn: 'Live stream', labelRu: 'Live-сессия', label: 'Live-сессия' } as any,
});

/**
 * CORE-879 — Live-сессия купленной программы как карточка афиши.
 *
 * Публичная выдача календаря (`daily-rhythm`, `calendar-events`) закрытую
 * Live-сессию программы не отдаёт никому, и купившему тоже: публичных
 * поверхностей у неё нет (CORE-988). Купившему она приходит только из личного
 * календаря `/api/user/my-calendar` с пометкой `byProgram` — отсюда афиша её и
 * берёт. Остальные записи личного календаря сюда не идут: свои «Я иду» человек
 * и так видит в публичной выдаче, а второй их источник поменял бы афишу для
 * всех, а не только для купивших.
 *
 * Статус считается по часам: в ответе личного календаря его нет, а отменённую
 * сессию сервер туда не кладёт вовсе. Тип — всегда Live-сессия: ответ его не
 * несёт, а метка Live-практики увела бы карточку в комнату с тем же id.
 */
export const programLiveToCalendarItem = (
  entry: CalendarItem,
  now: number = Date.now(),
): LivesCalendarItem | null => {
  if (entry.byProgram !== true) return null;
  const startMs = new Date(entry.startDateTimeUtc).getTime();
  if (!Number.isFinite(startMs)) return null;
  const durMs = (entry.durationMinutes || 60) * 60_000;
  const isEnded = startMs + durMs < now;
  const isLive = !isEnded && startMs - 60_000 <= now;
  const status: DailyRhythmItem['status'] = isEnded ? 'ended' : isLive ? 'live' : 'upcoming';

  return {
    id: entry.id,
    title: entry.title,
    startDateTimeUtc: entry.startDateTimeUtc,
    localStartTime: new Date(entry.startDateTimeUtc).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', hour12: false }),
    durationMinutes: entry.durationMinutes ?? null,
    coverImageUrl: entry.coverImageUrl ?? null,
    previewUrl: null,
    status,
    master: {
      id: entry.masterProfileId ?? 0,
      name: resolveName(entry.masterName),
      photoUrl: null,
      description: '',
      shortBio: '',
    } as any,
    category: null,
    categories: null,
    format: 'livestream',
    kind: { code: 'LiveStream', labelEn: 'Live stream', labelRu: 'Live-сессия', label: 'Live-сессия' } as any,
    _byProgram: true,
  };
};

/** CORE-879 — карточка пришла вместе с купленной программой. */
export const isProgramCalendarItem = (item: Pick<LivesCalendarItem, '_byProgram'>): boolean =>
  item._byProgram === true;

/**
 * Подтянут ли для дня `daily-rhythm`: признак — в дне уже есть Live-сессия из
 * него. Комнаты и офлайн-события приходят из своих источников и признаком не
 * служат. CORE-879 — Live-сессии купленных программ тоже: иначе день, где
 * есть одна закрытая сессия программы, так и остался бы без публичных.
 */
export const hasDailyRhythmItems = (items: LivesCalendarItem[]): boolean =>
  items.some(e => getRoomItemId(e) == null && !e._offlineEvent && !isProgramCalendarItem(e));

const offlineEventToDailyRhythmItem = (
  event: UnifiedEvent,
  user: User | null,
  ownMasterId: number | null,
): LivesCalendarItem | null => {
  const startTime = new Date(event.startDateUtc).getTime();
  if (!Number.isFinite(startTime)) return null;
  const durationMinutes = event.endDateUtc
    ? Math.max(0, Math.round((new Date(event.endDateUtc).getTime() - startTime) / 60_000))
    : null;
  const isOwnMasterEvent = !!user && ownMasterId != null && event.masterProfileId === ownMasterId;

  return {
    id: event.id,
    title: event.title,
    startDateTimeUtc: event.startDateUtc,
    localStartTime: new Date(event.startDateUtc).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', hour12: false }),
    durationMinutes,
    coverImageUrl: event.coverImageUrl,
    previewUrl: null,
    // CORE-1290 — «завершено» = прошёл КОНЕЦ события, а не его начало. Раньше
    // здесь стояло `startTime < now`, и многодневная офлайн-встреча (CORE-225)
    // получала статус 'ended' в первый же свой день. Заметно это не было,
    // потому что афиша статус вообще не спрашивала — она считала завершённость
    // по часам сама. Теперь спрашивает, и ложное 'ended' погасило бы идущее
    // событие; поле должно значить то же, что у комнат и у Lives.
    status: startTime + (durationMinutes ?? 0) * 60_000 < Date.now() ? 'ended' : 'upcoming',
    master: {
      id: event.masterProfileId ?? 0,
      name: isOwnMasterEvent ? getOwnMasterDisplayName(user) : resolveName(event.organizerName),
      photoUrl: isOwnMasterEvent ? user.avatarUrl || null : null,
      description: '',
      shortBio: '',
    } as any,
    category: event.tags?.join(', ') || event.tradition || null,
    categories: Array.isArray(event.tags) ? event.tags : null,
    format: 'offline',
    kind: { code: 'Offline', labelEn: 'Offline', labelRu: 'Офлайн', label: 'Офлайн' } as any,
    _offlineEvent: event,
  };
};

export const getFilterLabel = (
  activeTypeFilters: string[],
  includePathFilter: boolean,
  language: string,
) => {
  const isTypeFilterActive = activeTypeFilters.length > 0 && !activeTypeFilters.includes('all');
  if (!isTypeFilterActive && !includePathFilter) return '';
  const parts: string[] = [];
  // CORE-1865 — эфир и комната для человека одна «Live-сессия»: один пункт на оба технических типа.
  if (activeTypeFilters.includes('livestream') || activeTypeFilters.includes('liveroom')) parts.push(language === 'ru' ? 'Live-сессий' : language === 'es' ? 'Sesiones en vivo' : 'Live Sessions');
  if (activeTypeFilters.includes('offline')) parts.push(language === 'ru' ? 'Офлайн-событий' : language === 'es' ? 'Eventos presenciales' : 'Offline events');
  if (activeTypeFilters.includes('online')) parts.push(language === 'ru' ? 'Онлайн-событий' : language === 'es' ? 'Eventos en línea' : 'Online events');
  return parts.join(', ');
};

export const filterCalendarEvents = (
  events: LivesCalendarItem[],
  activeTypeFilters: string[],
  selectedPaths: string[],
) => {
  let filtered = [...events];
  if (selectedPaths.length > 0) {
    filtered = filtered.filter(e => eventItemMatchesDirections(e, selectedPaths));
  }
  const isTypeFilterActive = activeTypeFilters.length > 0 && !activeTypeFilters.includes('all');
  if (isTypeFilterActive) {
    filtered = filtered.filter(e => {
      const key = itemFormatKey(e);
      if (activeTypeFilters.includes('offline') && key === 'offline') return true;
      // CORE-1865 — фильтр «Live-сессии» покрывает оба технических типа: эфир и комнату.
      if (activeTypeFilters.includes('livestream') && (key === 'livestream' || key === 'liveroom')) return true;
      if (activeTypeFilters.includes('liveroom') && key === 'liveroom') return true;
      if (activeTypeFilters.includes('online') && (key === 'livestream' || key === 'liveroom' || key === 'online' || key === null)) return true;
      return false;
    });
  }
  return filtered;
};

type UseLivesCalendarOptions = {
  includeCurrentLives?: boolean;
  user: User | null;
  width: number;
  markedDates: Set<string>;
  reminderIds: Set<number>;
  /**
   * CORE-1134 — дни недельной афиши. Полный список Live-сессий приходит только
   * из daily-rhythm, который по умолчанию тянется для одного выбранного дня;
   * для режима «Вся неделя» его нужно подтянуть для каждого дня периода, где
   * есть события. Опция аддитивная: без неё поведение хука не меняется.
   */
  agendaDates?: string[];
  /**
   * CORE-1712 — комнаты, которые сервер показывает именно этому человеку, в том
   * числе «только подписчикам» мастеров, на которых он подписан: точки дней —
   * `calendar-events` с `includeRooms`, состав дня — `daily-rhythm` с
   * `includeLivePractices`. Без опции закрытая сессия попадала в календарь
   * только из `/watch-parties/my` — после «Напомнить» или входа. Включают
   * нативные экраны; без опции поведение хука не меняется.
   */
  includeFollowedRooms?: boolean;
};

// CORE-1712 — комната из daily-rhythm приходит без `_roomId`, а нативные экраны
// открывают комнату только по нему: без метки её id ушёл бы в EventDetail как id
// лайва и открыл бы чужую сессию (id у комнат и лайвов независимые). Метку типа
// ставим ту же, что у остальных комнат календаря.
const tagDailyRhythmRoom = (item: LivesCalendarItem): LivesCalendarItem =>
  item.kind?.code === 'LivePractice' && item._roomId == null
    ? { ...item, _roomId: item.id, kind: { ...ROOM_KIND } as any }
    : item;

export const useLivesCalendar = ({
  includeCurrentLives = false,
  user,
  width,
  markedDates,
  reminderIds,
  agendaDates,
  includeFollowedRooms = false,
}: UseLivesCalendarOptions) => {
  const today = useMemo(() => new Date(), []);
  const todayISO = toCalendarISO(today.getFullYear(), today.getMonth(), today.getDate());

  // AMB-231 — gates the calendar's «Live-практика» leg. When off:
  //   - room endpoint (getMyRooms) is not called;
  //   - any LivePractice items the daily-rhythm endpoint returns get
  //     filtered out before they reach monthlyData;
  //   - the `liveroom` type-filter chip stays in the API surface (consumer
  //     decides whether to render it) but matches nothing.
  const livePracticesEnabled = useLivePracticesEnabled();
  // CORE-1712 — под тем же флагом, что и остальные комнаты календаря.
  const followedRooms = includeFollowedRooms && livePracticesEnabled;

  const [viewedDate, setViewedDate] = useState(new Date());
  const year = viewedDate.getFullYear();
  const month = viewedDate.getMonth();
  // The month(s) whose days are currently visible on the date strip — drives
  // the header label. Usually one month, two while the visible window straddles
  // a boundary. Seeded with today's month for the initial (un-scrolled) view.
  const [visibleMonths, setVisibleMonths] = useState<MonthRef[]>(
    () => [{ year: today.getFullYear(), month: today.getMonth() }],
  );
  const [selectedDateStr, setSelectedDateStr] = useState<string>(todayISO);
  const [monthlyData, setMonthlyData] = useState<Record<string, LivesCalendarItem[]>>({});
  const [myRoomIds, setMyRoomIds] = useState<Set<number>>(new Set());
  const [monthDates, setMonthDates] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [activeTypeFilters, setActiveTypeFilters] = useState<string[]>([]);
  const [selectedPaths, setSelectedPaths] = useState<string[]>([]);
  const [openDropdown, setOpenDropdown] = useState<'paths' | null>(null);
  const [showMyEvents, setShowMyEvents] = useState(false);
  const {
    offlineRegistrationIds,
    offlineRegistrationPendingIds,
    offlineRegistrationErrorIds,
    registerOfflineEvent,
    unregisterOfflineEvent,
    toggleOfflineEventRegistration,
    seedOfflineEventRegistration,
  } = useCalendar();
  const [userTimezone, setUserTimezone] = useState<string>(getCachedTimezone());
  const [ownMasterId, setOwnMasterId] = useState<number | null>(user?.masterProfileId ?? null);
  const scrollRef = useRef<ScrollView>(null);
  // Latest known scroll position + viewport width of the strip, kept fresh from
  // onScroll/onLayout. The arrow buttons read this to page by a screenful.
  const scrollStateRef = useRef({ scrollX: 0, viewportW: width });
  // True while a programmatic scrollTo animation is in flight. The animation
  // emits onScroll events whose intermediate frames still show the *old*
  // months; without this guard those frames would call setViewedDate and undo
  // the navigation we just triggered (the «click twice» bug). The header
  // (visibleMonths) still updates during the animation — only data-loading
  // (viewedDate) is held until the user scrolls manually.
  const programmaticScrollRef = useRef(false);
  const programmaticScrollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const beginProgrammaticScroll = useCallback(() => {
    programmaticScrollRef.current = true;
    if (programmaticScrollTimer.current) clearTimeout(programmaticScrollTimer.current);
    programmaticScrollTimer.current = setTimeout(() => {
      programmaticScrollRef.current = false;
    }, 450);
  }, []);
  useEffect(() => () => {
    if (programmaticScrollTimer.current) clearTimeout(programmaticScrollTimer.current);
  }, []);

  const isMobileCalendar = width < 768;
  // CORE-778 — this strip is nested inside the calendar card. Its available
  // width excludes both the card margins and the strip's own horizontal
  // padding; using the viewport width clipped the seventh day on mobile.
  const mobileCalendarInnerWidth = Math.max(280, width - 48);
  const dateCellW = isMobileCalendar
    ? Math.max(32, Math.floor((mobileCalendarInnerWidth - 6 * 8) / 7))
    : 68;
  const dateCellGap = isMobileCalendar ? 8 : 16;
  const dateCellWidth = dateCellW + dateCellGap;

  const directionOptions = useDirections();
  const pathOptions = useMemo(() => directionOptions.map(name => ({ id: name, name })), [directionOptions]);

  const decorateOwnMasterItem = useCallback((item: LivesCalendarItem): LivesCalendarItem => {
    if (!user || ownMasterId == null || item.master?.id !== ownMasterId) return item;
    return {
      ...item,
      master: {
        ...item.master,
        name: getOwnMasterDisplayName(user),
        photoUrl: user.avatarUrl || item.master.photoUrl,
      },
    };
  }, [ownMasterId, user]);

  useEffect(() => {
    if (!user && showMyEvents) setShowMyEvents(false);
  }, [user, showMyEvents]);

  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setOwnMasterId(null);
      return;
    }
    if (user.masterProfileId != null) {
      setOwnMasterId(user.masterProfileId);
      return;
    }
    api
      .getMasterProfile()
      .then((profile) => {
        if (!cancelled) setOwnMasterId(profile?.id ?? null);
      })
      .catch(() => {
        if (!cancelled) setOwnMasterId(null);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    getUserTimezone().then(tz => setUserTimezone(tz));
  }, []);

  const dateRange = useMemo(() => {
    const dates: CalendarDateInfo[] = [];
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    // The strip must reach as far as the user can navigate. `loadMonthEvents`
    // preloads the viewed month + the next one, so extend the strip through the
    // end of that next month; otherwise navigating past ~3 months leaves the
    // selected day (and its markers) outside the rendered range — the header
    // shows the far month while the strip still shows today's dates. Keep a
    // 90-day floor so the default view is unchanged.
    const viewedNextMonthEnd = new Date(year, month + 2, 0);
    viewedNextMonthEnd.setHours(0, 0, 0, 0);
    const daysToCover = Math.round((viewedNextMonthEnd.getTime() - start.getTime()) / 86_400_000) + 1;
    const count = Math.max(90, daysToCover);
    for (let i = 0; i < count; i++) {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      dates.push({
        year: d.getFullYear(),
        month: d.getMonth(),
        day: d.getDate(),
        dateISO: toCalendarISO(d.getFullYear(), d.getMonth(), d.getDate()),
      });
    }
    return dates;
  }, [year, month]);

  const loadMonthEvents = useCallback(async () => {
    try {
      setIsLoading(true);

      const nextMo = (month + 1) % 12;
      const nextYr = month === 11 ? year + 1 : year;
      const monthsToLoad = [
        { y: year, m: month },
        { y: nextYr, m: nextMo },
      ];

      const newMap: Record<string, LivesCalendarItem[]> = {};
      const nextMonthDates = new Set<string>();

      // Empty day cells for the two visible months up front (sync) so every
      // date key exists even when a source returns nothing.
      for (const ml of monthsToLoad) {
        const daysCount = getDaysInMonth(ml.y, ml.m);
        for (let day = 1; day <= daysCount; day++) {
          newMap[toCalendarISO(ml.y, ml.m, day)] = [];
        }
      }

      // CORE-609 — the first navigation to «Все события» hung ~15s because the
      // month load fired its independent sources SEQUENTIALLY (2× calendar-dots,
      // my-rooms, current-lives, offline-events); their latencies summed, and on
      // a loaded dev box each ~0.4s call stretched to seconds → a ~5× wall-time
      // penalty on the very first load (day-to-day navigation is a single
      // getDailyRhythm call, so it felt fine). Fire all sources in parallel —
      // first-load wall time is now the slowest single call, not the sum. Each
      // source keeps its own .catch so one failure can't take down the others
      // (unchanged resilience). Merge stays sequential/sync below.
      const monthStartMs = new Date(year, month, 1).getTime();
      const monthEndMs = new Date(nextYr, nextMo + 1, 0, 23, 59, 59, 999).getTime();

      const dotsP = Promise.all(monthsToLoad.map(ml => {
        const daysCount = getDaysInMonth(ml.y, ml.m);
        const from = `${ml.y}-${String(ml.m + 1).padStart(2, '0')}-01`;
        const to = `${ml.y}-${String(ml.m + 1).padStart(2, '0')}-${String(daysCount).padStart(2, '0')}`;
        // Calendar still renders without dots if this fails.
        return api.getCalendarEvents(from, to, followedRooms ? { includeRooms: true } : undefined)
          .then(r => r.dates || []).catch(() => [] as string[]);
      }));
      // AMB-231 — only hit my-rooms when the flag is on (and a user is signed in).
      const roomsP: Promise<{ items?: RoomResponse[] }> = (livePracticesEnabled && user)
        ? api.getMyRooms({ limit: 100 }).catch(() => ({ items: [] as RoomResponse[] }))
        : Promise.resolve({ items: [] as RoomResponse[] });
      // CORE-748 — the platform-wide discovery leg. Without it the calendar's
      // only room source was getMyRooms (host OR participant OR subscriber),
      // so a published public Live-сессия was visible to its creator and to
      // whoever had already RSVP'd — and to nobody else. Anonymous-safe: the
      // BE returns only Public rooms here, so no auth is required and none of
      // the «только для подписчиков» / private sessions leak through. No date
      // params on this endpoint — the list is the platform's whole non-ended
      // public set, so we pull it whole and clip to the month window with the
      // same predicate my-rooms uses (see utils/calendarRooms).
      const publicRoomsP: Promise<{ items?: RoomResponse[] }> = livePracticesEnabled
        ? api.getPublicRooms({ limit: 100 }).catch(() => ({ items: [] as RoomResponse[] }))
        : Promise.resolve({ items: [] as RoomResponse[] });
      const livesP: Promise<CurrentLiveResponse[]> = includeCurrentLives
        ? api.getCurrentLives().catch(() => [] as CurrentLiveResponse[])
        : Promise.resolve([] as CurrentLiveResponse[]);
      const offlineP: Promise<{ items?: UnifiedEvent[] }> = api.getEvents({
        isOnline: false,
        from: new Date(monthStartMs).toISOString(),
        to: new Date(monthEndMs).toISOString(),
        limit: 100,
      }).catch(() => ({ items: [] as UnifiedEvent[] }));
      // CORE-879 — Live-сессии купленных программ: публичная выдача их не
      // отдаёт (см. programLiveToCalendarItem), и без этого источника купивший
      // не находил в «Календаре» ни одной своей сессии программы.
      const programP: Promise<CalendarItem[]> = user
        ? Promise.all(monthsToLoad.map(ml =>
          api.getUserCalendar({ month: `${ml.y}-${String(ml.m + 1).padStart(2, '0')}` })
            .catch(() => [] as CalendarItem[]),
        )).then(lists => lists.flat())
        : Promise.resolve([] as CalendarItem[]);

      const [dotsResults, myRoomsRes, publicRoomsRes, currentLives, offlineEventsRes, programEntries] =
        await Promise.all([dotsP, roomsP, publicRoomsP, livesP, offlineP, programP]);

      // Dots.
      dotsResults.flat().forEach(d => nextMonthDates.add(d));

      // Rooms (AMB-231 — skip the merge when the flag is off; otherwise clear
      // myRoomIds so getIsMyEvent doesn't surface stale ownership from a
      // previous flag-on session).
      if (livePracticesEnabled) {
        const myRooms = ((myRoomsRes?.items || []) as RoomResponse[])
          .filter(r => isRoomInWindow(r, monthStartMs, monthEndMs));
        // myRoomIds stays sourced from my-rooms alone: it drives the personal
        // «моё / я иду» marker (getIsMyEvent), which must NOT follow from a
        // session merely being publicly listed. CORE-748's whole point is that
        // discovery and RSVP are separate concerns.
        setMyRoomIds(new Set(myRooms.map(r => r.id)));

        const rooms = mergeCalendarRooms(
          myRooms,
          (publicRoomsRes?.items || []) as RoomResponse[],
          monthStartMs,
          monthEndMs,
        );

        rooms.forEach(room => {
          const item = roomToDailyRhythmItem(room, user);
          if (!item) return;
          const dateISO = getItemDateISO(item);
          if (!dateISO) return;
          newMap[dateISO] = mergeUniqueEvents(newMap[dateISO] || [], [item]);
          // CORE-748 — rooms never used to mark their day. The dot endpoint
          // (/api/home/calendar-events) only knows Lives, so a day whose only
          // event was a Live-сессия stayed blank on the date strip and in the
          // month grid — nothing invited the user to open it. Offline events
          // have always marked their days; rooms now do the same.
          nextMonthDates.add(dateISO);
        });
      } else {
        setMyRoomIds(new Set());
      }

      // CORE-879 — Live-сессии купленных программ. Кладутся раньше идущих
      // эфиров: при совпадении id остаётся первая копия, а метка программы
      // должна выжить — по ней карточка не предлагает «Убрать».
      programEntries.forEach(entry => {
        const item = programLiveToCalendarItem(entry);
        if (!item) return;
        const dateISO = getItemDateISO(item);
        if (!dateISO) return;
        newMap[dateISO] = mergeUniqueEvents(newMap[dateISO] || [], [item]);
        nextMonthDates.add(dateISO);
      });

      // Current lives — belong on today's surface even if they started before
      // local midnight.
      if (includeCurrentLives) {
        const currentLiveItems = currentLives
          .map(currentLiveToDailyRhythmItem)
          .map(decorateOwnMasterItem);
        currentLiveItems.forEach(item => {
          newMap[todayISO] = mergeUniqueEvents(newMap[todayISO] || [], [item]);
          nextMonthDates.add(todayISO);
        });
      }

      // Offline events.
      const nextRegisteredIds = new Set<number>();
      (offlineEventsRes.items || []).forEach(event => {
        // CORE-1067 — /api/events already tells us whether this user is going;
        // without seeding, a reloaded page shows «Зарегистрироваться» to someone
        // who is already registered and re-POSTs on tap.
        if (event.isRegistered) {
          nextRegisteredIds.add(event.id);
        }
        // CORE-226 — private offline events (CORE-89) are returned to their
        // OWNER by /api/events (for the management «События» tab), but must
        // NOT appear on the platform calendar. AC: public → calendar,
        // private → tab only. Anonymous callers never receive them.
        if (!isPublicDiscoverableEvent(event)) return;
        const item = offlineEventToDailyRhythmItem(event, user, ownMasterId);
        if (!item) return;
        // CORE-225 — a multi-day offline event must appear on every day it
        // spans, not only its start day.
        getItemSpanDaysISO(item).forEach(dateISO => {
          newMap[dateISO] = mergeUniqueEvents(newMap[dateISO] || [], [item]);
          nextMonthDates.add(dateISO);
        });
      });

      setMonthlyData(prev => ({ ...prev, ...newMap }));
      setMonthDates(nextMonthDates);
      // Additive only: a registration made locally in this session must not be
      // dropped by a month that was fetched before it landed.
      nextRegisteredIds.forEach(id => seedOfflineEventRegistration(id, true));
    } catch (err) {
      console.log('Calendar fetch error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [month, user, year, livePracticesEnabled, followedRooms, includeCurrentLives, todayISO, ownMasterId, decorateOwnMasterItem, seedOfflineEventRegistration]);

  useEffect(() => {
    if (!selectedDateStr) return;
    if (!monthDates.has(selectedDateStr)) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await api.getDailyRhythm(
          selectedDateStr,
          userTimezone,
          followedRooms ? { includeLivePractices: true } : undefined,
        );
        if (cancelled) return;
        const items = (data.items || [])
          .filter(item => getItemDateISO(item) === selectedDateStr)
          // AMB-231 — strip Live-практик that the daily-rhythm endpoint
          // mixes in alongside Live-сессии. Belt-and-suspenders on top of
          // the rooms-merge gate above so any future BE source for the
          // same kind code stays hidden too.
          .filter(item => livePracticesEnabled || item.kind?.code !== 'LivePractice')
          .map(item => (followedRooms ? tagDailyRhythmRoom(item as LivesCalendarItem) : item as LivesCalendarItem))
          .map(item => decorateOwnMasterItem(item));
        if (items.length === 0) return;
        setMonthlyData(prev => {
          const existing = prev[selectedDateStr] || [];
          if (hasDailyRhythmItems(existing)) return prev;
          return { ...prev, [selectedDateStr]: mergeUniqueEvents(existing, items) };
        });
      } catch {
        // Non-fatal.
      }
    })();
    return () => { cancelled = true; };
  }, [monthDates, selectedDateStr, userTimezone, livePracticesEnabled, followedRooms, decorateOwnMasterItem]);

  // CORE-1134 — daily-rhythm для всех дней афиши, у которых есть «точка».
  // Тот же merge и тот же guard, что у эффекта выбранного дня выше; уже
  // подтянутые дни помнит ref, который сбрасывается вместе с monthDates
  // (loadMonthEvents перезаписывает дни пустыми массивами и меняет monthDates,
  // так что после перезагрузки месяца дни подтягиваются заново).
  const agendaFetchedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    agendaFetchedRef.current = new Set();
  }, [monthDates]);
  const agendaDatesKey = agendaDates ? agendaDates.join(',') : '';
  useEffect(() => {
    if (!agendaDatesKey) return;
    const targets = agendaDatesKey
      .split(',')
      .filter(dateISO => monthDates.has(dateISO) && !agendaFetchedRef.current.has(dateISO));
    if (targets.length === 0) return;
    targets.forEach(dateISO => agendaFetchedRef.current.add(dateISO));
    // Без отмены: ответ для дня, с которого пользователь уже ушёл, всё равно
    // ложится в monthlyData под своим ключом и пригодится при возврате.
    targets.forEach(dateISO => {
      api.getDailyRhythm(dateISO, userTimezone, followedRooms ? { includeLivePractices: true } : undefined)
        .then(data => {
          const items = (data.items || [])
            .filter(item => getItemDateISO(item) === dateISO)
            .filter(item => livePracticesEnabled || item.kind?.code !== 'LivePractice')
            .map(item => (followedRooms ? tagDailyRhythmRoom(item as LivesCalendarItem) : item as LivesCalendarItem))
            .map(item => decorateOwnMasterItem(item));
          if (items.length === 0) return;
          setMonthlyData(prev => {
            const existing = prev[dateISO] || [];
            if (hasDailyRhythmItems(existing)) return prev;
            return { ...prev, [dateISO]: mergeUniqueEvents(existing, items) };
          });
        })
        .catch(() => {
          // Non-fatal — the day keeps its offline/room items.
        });
    });
  }, [agendaDatesKey, monthDates, userTimezone, livePracticesEnabled, followedRooms, decorateOwnMasterItem]);

  useEffect(() => {
    loadMonthEvents();
    const liveSub = DeviceEventEmitter.addListener('live_published', loadMonthEvents);
    const liveUpd = DeviceEventEmitter.addListener('live_updated', loadMonthEvents);
    const roomSub = DeviceEventEmitter.addListener('room_created', loadMonthEvents);
    return () => {
      liveSub.remove();
      liveUpd.remove();
      roomSub.remove();
    };
  }, [loadMonthEvents]);

  // Centre the strip on the selected day when the *selection* changes (day tap,
  // deep link, reset). Deliberately NOT keyed on dateRange: month navigation
  // grows dateRange, and re-running this on that change would yank the strip
  // back to the selected day and fight the arrow scroll. The index is derived
  // arithmetically (days since today) so dateRange isn't needed here.
  useEffect(() => {
    const parts = selectedDateStr.split('-').map(Number);
    if (parts.length !== 3 || parts.some(Number.isNaN)) return;
    const sel = new Date(parts[0], parts[1] - 1, parts[2]);
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const index = Math.round((sel.getTime() - start.getTime()) / 86_400_000);
    if (index >= 0) {
      beginProgrammaticScroll();
      scrollRef.current?.scrollTo({ x: Math.max(0, index * dateCellWidth - width / 2 + 32), animated: true });
    }
  }, [beginProgrammaticScroll, dateCellWidth, selectedDateStr, width]);

  // Page the strip by one screenful. The next screen starts on the day the
  // current one ended (one-day overlap for continuity), regardless of month
  // boundaries — so a screen showing only part of a month doesn't jump to the
  // 1st of the next month. viewedDate is moved to the first visible day's month
  // so loadMonthEvents covers the new screen and dateRange grows ahead of it.
  const pageStrip = useCallback((dir: 1 | -1) => {
    const { scrollX, viewportW } = scrollStateRef.current;
    const perPage = Math.max(1, Math.floor(viewportW / dateCellWidth));
    const overlap = perPage > 1 ? 1 : 0;
    const currentFirst = Math.max(0, Math.round(scrollX / dateCellWidth));
    const newFirst = Math.max(0, currentFirst + dir * (perPage - overlap));

    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const firstDate = new Date(start);
    firstDate.setDate(firstDate.getDate() + newFirst);
    setViewedDate(new Date(firstDate.getFullYear(), firstDate.getMonth(), 1));

    beginProgrammaticScroll();
    scrollRef.current?.scrollTo({ x: newFirst * dateCellWidth, animated: true });
  }, [beginProgrammaticScroll, dateCellWidth]);

  // Distinct months whose day cells overlap the [scrollX, scrollX + viewportW]
  // viewport. Cells start after the contentContainer's leading padding (12
  // mobile / 24 desktop, mirrors LivesCalendarDateStrip) and each occupies
  // dateCellWidth.
  const computeVisibleMonths = useCallback((scrollX: number, viewportW: number): MonthRef[] => {
    const leadPad = isMobileCalendar ? 12 : 24;
    // A cell counts as visible when its CENTRE is inside the viewport — a sliver
    // poking past the left edge shouldn't drag its month into the header.
    const halfCell = dateCellW / 2;
    const firstIdx = Math.max(0, Math.ceil((scrollX - leadPad - halfCell) / dateCellWidth));
    const lastIdx = Math.min(
      dateRange.length - 1,
      Math.floor((scrollX + viewportW - leadPad - halfCell) / dateCellWidth),
    );
    const months: MonthRef[] = [];
    const seen = new Set<string>();
    for (let i = firstIdx; i <= lastIdx; i++) {
      const d = dateRange[i];
      if (!d) continue;
      const key = `${d.year}-${d.month}`;
      if (seen.has(key)) continue;
      seen.add(key);
      months.push({ year: d.year, month: d.month });
    }
    return months;
  }, [dateCellW, dateCellWidth, dateRange, isMobileCalendar]);

  const applyVisibleMonths = useCallback((months: MonthRef[]) => {
    if (months.length === 0) return;
    setVisibleMonths(prev =>
      prev.length === months.length &&
      prev.every((m, i) => m.year === months[i].year && m.month === months[i].month)
        ? prev
        : months,
    );
    // The first visible month drives data loading: loadMonthEvents fetches it
    // plus the following one, so both months on the strip stay covered and
    // dateRange extends far enough to keep the forward scroll continuous.
    // Held back during a programmatic scroll so the in-flight animation can't
    // undo an arrow-triggered navigation.
    if (programmaticScrollRef.current) return;
    const lead = months[0];
    if (lead.month !== month || lead.year !== year) {
      setViewedDate(new Date(lead.year, lead.month, 1));
    }
  }, [month, year]);

  const handleDateStripScroll = useCallback((event: any) => {
    const { contentOffset, layoutMeasurement } = event.nativeEvent;
    // The strip's actual viewport (not the full window) is what's visible, so
    // measure against it. `width` is only a fallback for environments that
    // don't report layoutMeasurement.
    const viewportW = layoutMeasurement?.width || width;
    scrollStateRef.current = { scrollX: contentOffset.x, viewportW };
    applyVisibleMonths(computeVisibleMonths(contentOffset.x, viewportW));
  }, [applyVisibleMonths, computeVisibleMonths, width]);

  // The strip can already show two months on first paint (e.g. 23 июн … 7 июл),
  // but onScroll hasn't fired yet — derive the initial header from the real
  // viewport width once the ScrollView is laid out.
  const handleDateStripLayout = useCallback((event: any) => {
    const viewportW = event.nativeEvent?.layout?.width || width;
    scrollStateRef.current.viewportW = viewportW;
    applyVisibleMonths(computeVisibleMonths(scrollStateRef.current.scrollX, viewportW));
  }, [applyVisibleMonths, computeVisibleMonths, width]);

  // Arrows page the strip by a screenful (see pageStrip). Names kept as
  // prev/nextMonth to match the existing screen props, but they no longer snap
  // to month boundaries. The selected day is left untouched — paging shouldn't
  // move the selection, and not touching it keeps the centre-on-select effect
  // from firing and fighting this scroll.
  const prevMonth = useCallback(() => pageStrip(-1), [pageStrip]);
  const nextMonth = useCallback(() => pageStrip(1), [pageStrip]);

  const handleToggleType = useCallback((type: string) => {
    setActiveTypeFilters(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]);
  }, []);

  const handleTogglePath = useCallback((key: string) => {
    setSelectedPaths(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  }, []);

  const resetFilters = useCallback(() => {
    setActiveTypeFilters([]);
    setSelectedPaths([]);
    setOpenDropdown(null);
    setShowMyEvents(false);
  }, []);

  const resetToToday = useCallback(() => {
    resetFilters();
    setSelectedDateStr(todayISO);
    setViewedDate(new Date());
    setVisibleMonths([{ year: today.getFullYear(), month: today.getMonth() }]);
    beginProgrammaticScroll();
    scrollRef.current?.scrollTo({ x: 0, animated: true });
  }, [beginProgrammaticScroll, resetFilters, today, todayISO]);

  // Jump the strip to a specific month (from the month/year picker). The strip
  // always starts at today, so the target's scroll x is just its day-offset
  // from today × the cell pitch — no need to wait for the range to regrow.
  const jumpToMonth = useCallback((y: number, m: number) => {
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const isCurrent = y === today.getFullYear() && m === today.getMonth();
    const targetDay = isCurrent ? today.getDate() : 1;
    const target = new Date(y, m, targetDay);
    const dayIndex = Math.max(0, Math.round((target.getTime() - startOfToday.getTime()) / 86_400_000));
    setViewedDate(new Date(y, m, 1));
    setSelectedDateStr(toCalendarISO(y, m, targetDay));
    setVisibleMonths([{ year: y, month: m }]);
    beginProgrammaticScroll();
    // The date range regrows to include the target on this state change; scroll
    // once it's committed so the ScrollView content is wide enough.
    setTimeout(() => scrollRef.current?.scrollTo({ x: dayIndex * dateCellWidth, animated: true }), 60);
  }, [today, beginProgrammaticScroll, dateCellWidth]);

  // CORE-1134 — «Присоединиться» у Live-практики записывает на неё прямо из
  // календаря (тот же subscribe, что на странице анонса), «Я иду» — отписывает.
  // myRoomIds обновляется сразу, чтобы карточка переключилась без перезагрузки.
  const toggleRoomRsvp = useCallback(async (roomId: number, subscribed: boolean) => {
    if (!user) return;
    if (subscribed) {
      await api.unsubscribeFromRoom(roomId);
      setMyRoomIds(prev => { const next = new Set(prev); next.delete(roomId); return next; });
    } else {
      await api.subscribeToRoom(roomId);
      setMyRoomIds(prev => new Set(prev).add(roomId));
    }
  }, [user]);
  const selectedDateEvents = useMemo(
    () => mergeUniqueEvents([], monthlyData[selectedDateStr] || []),
    [monthlyData, selectedDateStr],
  );

  const getIsMyEvent = useCallback((item: LivesCalendarItem) => {
    if (item._offlineEvent) return offlineRegistrationIds.has(item._offlineEvent.id);
    // CORE-879 — купленная программа и есть «я иду»: прошедшая сессия
    // программы в reminderIds не попадает, а в «Моих событиях» ей место.
    if (isProgramCalendarItem(item)) return true;
    const roomId = getRoomItemId(item);
    if (roomId != null) {
      return myRoomIds.has(roomId) || item._isMine === true || item._isMyRoom === true;
    }
    if (item.kind?.code && item.kind.code !== 'LiveStream') return false;
    return reminderIds.has(item.id);
  }, [myRoomIds, offlineRegistrationIds, reminderIds]);

  const getDayMarkers = useCallback((dateISO: string) => {
    const dayEvents = monthlyData[dateISO] || [];
    const hasEvents = monthDates.has(dateISO) || dayEvents.length > 0;
    const hasReminder = markedDates.has(dateISO) || dayEvents.some(getIsMyEvent);
    return { dayEvents, hasEvents, hasReminder };
  }, [getIsMyEvent, markedDates, monthDates, monthlyData]);

  return {
    activeTypeFilters,
    dateCellGap,
    dateCellW,
    dateRange,
    getDayMarkers,
    getIsMyEvent,
    handleDateStripLayout,
    handleDateStripScroll,
    handleTogglePath,
    handleToggleType,
    isLoading,
    isMobileCalendar,
    month,
    monthDates,
    monthlyData,
    myRoomIds,
    offlineRegistrationIds,
    offlineRegistrationPendingIds,
    offlineRegistrationErrorIds,
    jumpToMonth,
    nextMonth,
    openDropdown,
    pathOptions,
    prevMonth,
    resetFilters,
    resetToToday,
    registerOfflineEvent,
    unregisterOfflineEvent,
    toggleOfflineEventRegistration,
    scrollRef,
    selectedDateEvents,
    selectedDateStr,
    selectedPaths,
    setOpenDropdown,
    setSelectedDateStr,
    setSelectedPaths,
    setShowMyEvents,
    setViewedDate,
    showMyEvents,
    today,
    todayISO,
    toggleRoomRsvp,
    viewedDate,
    visibleMonths,
    year,
  };
};
